import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtRM } from "../../lib/format";
import { useLang } from "../../lib/i18n";
import { PageTitle, StatusBadge, Spinner } from "../../components/Shell";
import { toast } from "sonner";
import { Plus, Search, X, Eye } from "lucide-react";

export default function Members() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", ic_number: "", email: "", phone: "", address: "" });
  const [portalView, setPortalView] = useState(null);
  const { lang, t } = useLang();

  const load = (query = q) => api.get(`/tenant/members?q=${encodeURIComponent(query)}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(""); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenant/members", form);
      toast.success(lang === "bm" ? "Ahli ditambah — menunggu kelulusan" : "Member added — pending approval");
      setShowForm(false);
      setForm({ name: "", ic_number: "", email: "", phone: "", address: "" });
      load("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openPortal = async (m) => {
    try {
      const r = await api.get(`/tenant/members/${m.member_id}/portal-view`);
      setPortalView(r.data);
    } catch {
      toast.error("Error");
    }
  };

  const setKyc = async (m, kyc_status) => {
    try {
      await api.put(`/tenant/members/${m.member_id}`, { kyc_status });
      toast.success(`KYC → ${kyc_status}`);
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (!data) return <Spinner />;

  return (
    <div data-testid="tenant-members">
      <PageTitle title={lang === "bm" ? "Pengurusan Ahli" : "Member Management"} sub={`${data.total} ahli berdaftar`}>
        <button data-testid="add-member-btn" onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-brand-deep text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-brand-dark transition-colors">
          <Plus className="w-4 h-4" /> {lang === "bm" ? "Ahli Baharu" : "New Member"}
        </button>
      </PageTitle>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="member-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder={t("common.search")}
          className="w-full rounded-xl border border-input bg-card pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-deep/30"
        />
      </div>

      <div className="bg-card rounded-xl border border-brand-deep/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-brand-deep/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3">No. Ahli</th>
              <th className="px-5 py-3">{t("common.name")}</th>
              <th className="px-5 py-3">KYC</th>
              <th className="px-5 py-3">{t("common.status")}</th>
              <th className="px-5 py-3 text-right">Saham</th>
              <th className="px-5 py-3 text-right">Yuran</th>
              <th className="px-5 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.member_id} className="border-b border-brand-deep/5 hover:bg-brand-light/40 transition-colors" data-testid={`member-row-${m.member_no}`}>
                <td className="px-5 py-3 font-mono text-xs font-semibold text-brand-deep">{m.member_no}</td>
                <td className="px-5 py-3">
                  <div className="font-medium text-brand-dark">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.ic_number || m.phone || "-"}</div>
                </td>
                <td className="px-5 py-3"><StatusBadge status={m.kyc_status} /></td>
                <td className="px-5 py-3"><StatusBadge status={m.status} /></td>
                <td className="px-5 py-3 text-right font-mono">{fmtRM(m.share_balance)}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtRM(m.fee_balance)}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-3">
                    <button data-testid={`member-portal-${m.member_no}`} onClick={() => openPortal(m)} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-800 transition-colors">
                      <Eye className="w-3 h-3" /> Portal
                    </button>
                    {m.kyc_status === "pending" && (
                      <button data-testid={`kyc-verify-${m.member_no}`} onClick={() => setKyc(m, "verified")} className="text-xs font-semibold text-brand-deep hover:text-gold transition-colors">
                        Sahkan KYC
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.members.length === 0 && <p className="px-5 py-8 text-center text-sm text-muted-foreground">Tiada ahli dijumpai.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <form onSubmit={create} className="relative bg-card rounded-2xl p-6 w-full max-w-md animate-fade-up" data-testid="member-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-brand-dark">Ahli Baharu</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input data-testid="member-name-input" required placeholder="Nama Penuh *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input data-testid="member-ic-input" placeholder="No. Kad Pengenalan" value={form.ic_number} onChange={(e) => setForm({ ...form, ic_number: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input data-testid="member-email-input" type="email" placeholder="Emel (untuk akses Portal Ahli)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input data-testid="member-phone-input" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />
              <input placeholder="Alamat" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Ahli baharu memerlukan kelulusan (maker-checker) sebelum aktif.</p>
            <button data-testid="member-submit-btn" type="submit" className="mt-4 w-full bg-brand-deep text-white rounded-xl py-2.5 text-sm font-bold hover:bg-brand-dark transition-colors">Simpan & Hantar Kelulusan</button>
          </form>
        </div>
      )}

      {portalView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPortalView(null)} />
          <div className="relative bg-card rounded-2xl border border-brand-deep/15 w-full max-w-2xl max-h-[85vh] overflow-y-auto animate-fade-up" data-testid="member-portal-modal">
            <div className="p-5 border-b border-brand-deep/10 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-brand-dark">{portalView.member.name}</h3>
                  <StatusBadge status={portalView.member.status} />
                  <StatusBadge status={portalView.member.kyc_status} />
                </div>
                <div className="text-xs text-muted-foreground font-mono mt-1">{portalView.member.member_no} • Paparan Portal Ahli (read-only)</div>
              </div>
              <button data-testid="member-portal-close" onClick={() => setPortalView(null)} className="text-muted-foreground hover:text-brand-dark"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="rounded-xl bg-brand-light p-4">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Baki Saham</div>
                  <div className="font-mono text-lg font-bold text-brand-deep mt-1">{fmtRM(portalView.member.share_balance)}</div>
                </div>
                <div className="rounded-xl bg-amber-50 p-4">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Yuran Tertunggak</div>
                  <div className="font-mono text-lg font-bold text-amber-600 mt-1">{fmtRM(portalView.member.fee_balance)}</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-xs">
                <div>
                  <div className="font-bold uppercase tracking-wider text-muted-foreground mb-2">Saham</div>
                  {portalView.transactions.slice(0, 5).map((tx) => (
                    <div key={tx.txn_id} className="flex justify-between py-1.5 border-b border-brand-deep/5">
                      <span className="capitalize">{tx.type}</span>
                      <span className="font-mono">{fmtRM(tx.amount)}</span>
                    </div>
                  ))}
                  {portalView.transactions.length === 0 && <p className="text-muted-foreground">Tiada.</p>}
                </div>
                <div>
                  <div className="font-bold uppercase tracking-wider text-muted-foreground mb-2">Yuran</div>
                  {portalView.fees.slice(0, 5).map((f) => (
                    <div key={f.fee_id} className="flex justify-between items-center py-1.5 border-b border-brand-deep/5">
                      <span>{f.fee_type}</span>
                      <StatusBadge status={f.status} />
                    </div>
                  ))}
                  {portalView.fees.length === 0 && <p className="text-muted-foreground">Tiada.</p>}
                </div>
                <div>
                  <div className="font-bold uppercase tracking-wider text-muted-foreground mb-2">Resit</div>
                  {portalView.payments.slice(0, 5).map((p) => (
                    <div key={p.payment_id} className="flex justify-between py-1.5 border-b border-brand-deep/5">
                      <span className="font-mono">{p.receipt_no}</span>
                      <span className="font-mono">{fmtRM(p.amount)}</span>
                    </div>
                  ))}
                  {portalView.payments.length === 0 && <p className="text-muted-foreground">Tiada.</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
